import { useMemo } from 'react'
import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { buildFilterKey } from '@/services/filterEngine'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'

// Most/Least topic pairs (will be sorted by net score)
// Labels reflect actual survey wording
const MOST_LEAST_TOPICS = [
  { topic: 'Healthcare', label: 'Healthcare/Social programs' },
  { topic: 'IndivFreedom', label: 'Individual freedom' },
  { topic: 'PeaceOrder', label: 'Peace, order, democracy' },
  { topic: 'NatureEnv', label: 'Nature & environment' },
  { topic: 'Tolerance', label: 'Tolerance & diversity' },
  { topic: 'NaturalResources', label: 'Natural resources' },
  { topic: 'Education', label: 'Education & training' },
  { topic: 'SelfReliance', label: 'Self-reliance' },
  { topic: 'Peacekeeping', label: 'Peacekeeping' },
  { topic: 'Hockey', label: 'Hockey' },
  { topic: 'CoopTeamwork', label: 'Cooperation & teamwork' },
  { topic: 'Creativity', label: 'Creativity & innovation' },
  { topic: 'IndigenousHeritage', label: 'Indigenous heritage' },
  { topic: 'StrongMilitary', label: 'Strong military' },
]

// Topline report totals (rounded) - used for Total view only
// From V4 (Most Important) and V5 (Least Important) in topline report
const TOPLINE_TOTALS: Record<string, { most: number; least: number }> = {
  Healthcare: { most: 45.4, least: 8.6 },
  IndivFreedom: { most: 39.3, least: 11 },
  PeaceOrder: { most: 37.6, least: 10.6 },
  NatureEnv: { most: 28.4, least: 9.4 },
  Tolerance: { most: 22.8, least: 33.9 },
  NaturalResources: { most: 20.4, least: 10.3 },
  Education: { most: 16, least: 11.3 },
  SelfReliance: { most: 16, least: 30.3 },
  Peacekeeping: { most: 16, least: 18.2 },
  Hockey: { most: 13.4, least: 50.8 },
  CoopTeamwork: { most: 9.8, least: 18.7 },
  Creativity: { most: 10.2, least: 20.3 },
  IndigenousHeritage: { most: 10.3, least: 32 },
  StrongMilitary: { most: 7.6, least: 35.4 },
}

// Response order - Third (darkest) should be closest to center on both sides
const RESPONSE_ORDER_MOST = ['Third', 'Second', 'First'] as const  // Left bar: dark to light (away from center)
const RESPONSE_ORDER_LEAST = ['First', 'Second', 'Third'] as const // Right bar: light to dark (toward center)

// Blue gradient for Most Important (First = darkest/most affirmative)
const MOST_COLORS: Record<string, string> = {
  'First': '#005383',   // Dark blue (most affirmative)
  'Second': '#006d9f',  // Medium blue
  'Third': '#0087bb',   // Light blue (least affirmative)
}

// Red gradient for Least Important (First = darkest/most affirmative)
const LEAST_COLORS: Record<string, string> = {
  'First': '#aa0503',   // Dark red (most affirmative)
  'Second': '#c42114',  // Medium red
  'Third': '#df3d26',   // Light red (least affirmative)
}

// Legend items for display (First = most affirmative = darkest)
const LEGEND_ITEMS_MOST = [
  { label: 'First', color: '#005383' },
  { label: 'Second', color: '#006d9f' },
  { label: 'Third', color: '#0087bb' },
]

const LEGEND_ITEMS_LEAST = [
  { label: 'First', color: '#aa0503' },
  { label: 'Second', color: '#c42114' },
  { label: 'Third', color: '#df3d26' },
]

export function MostLeast() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  // Get break definition
  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}

  // Get display name with alias support
  const getDisplayName = (value: string) => displayMap[value] || value

  // Helper to get total percentage for Most or Least (sum of First, Second, Third)
  const getTotalPercentage = (questionData: { responses: Array<{ label: string; percentage: number }> } | undefined) => {
    if (!questionData) return 0
    return questionData.responses
      .filter(r => ['First', 'Second', 'Third'].includes(r.label))
      .reduce((sum, r) => sum + r.percentage, 0)
  }

  // Sort topics by Total Most Important percentage (green bars) - highest first
  const sortedTopics = useMemo(() => {
    if (!data) return MOST_LEAST_TOPICS

    const totalFilterKey = buildFilterKey('total', 'total')

    return [...MOST_LEAST_TOPICS].sort((a, b) => {
      const aMost = data.questions[`ID_Most_${a.topic}`]?.data[totalFilterKey]
      const bMost = data.questions[`ID_Most_${b.topic}`]?.data[totalFilterKey]

      const aMostTotal = getTotalPercentage(aMost)
      const bMostTotal = getTotalPercentage(bMost)

      return bMostTotal - aMostTotal // Descending order (highest Most Important first)
    })
  }, [data])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          Most / Least Important Values
        </h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          Break: {breakName}
        </span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <p className="text-slate-600 dark:text-slate-300 mb-4">
          Which 3 items below are <strong>MOST/LEAST</strong> important to your sense of Canadian identity?
        </p>

        {/* Legend - Most (green) and Least (red) */}
        <div className="flex gap-x-8 mb-6 pb-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-4">
            <span className="text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Most:</span>
            {LEGEND_ITEMS_MOST.map(item => (
              <div key={`most-${item.label}`} className="flex items-center gap-1.5">
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-xs text-slate-600 dark:text-slate-400">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Least:</span>
            {LEGEND_ITEMS_LEAST.map(item => (
              <div key={`least-${item.label}`} className="flex items-center gap-1.5">
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-xs text-slate-600 dark:text-slate-400">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Column headers */}
        <div className="flex items-center mb-4 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {isBreakActive && <div className="w-32 shrink-0" />}
          <div className="flex-1 text-right pr-4">Most Important</div>
          <div className="w-28 text-center shrink-0"></div>
          <div className="flex-1 text-left pl-4">Least Important</div>
          <div className="w-16 shrink-0" />
        </div>

        <div className="space-y-8">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading...</p>
          ) : (
            sortedTopics.map(({ topic, label }) => {
              const mostQuestion = data.questions[`ID_Most_${topic}`]
              const leastQuestion = data.questions[`ID_Least_${topic}`]
              if (!mostQuestion || !leastQuestion) return null

              // Build rows for each break value
              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey(
                  'total',
                  selectedBreak,
                  isBreakActive ? value : undefined,
                  selectedProvince
                )
                let mostData = mostQuestion.data[filterKey]
                let leastData = leastQuestion.data[filterKey]

                // Fallback: try without province filter if no data found
                if ((!mostData || !leastData) && selectedProvince) {
                  const fallbackKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)
                  mostData = mostData || mostQuestion.data[fallbackKey]
                  leastData = leastData || leastQuestion.data[fallbackKey]
                }

                if (!mostData || !leastData) return null

                // Get percentages for ranking categories (excluding "Not selected")
                // Most: First→Second→Third (light to dark, toward center)
                const getMostSegments = () => {
                  return RESPONSE_ORDER_MOST.map(rank => {
                    const response = mostData!.responses.find(r => r.label === rank)
                    return { label: rank, percentage: response?.percentage || 0 }
                  }).filter(r => r.percentage > 0)
                }

                // Least: Third→Second→First (dark to light, away from center)
                const getLeastSegments = () => {
                  return RESPONSE_ORDER_LEAST.map(rank => {
                    const response = leastData!.responses.find(r => r.label === rank)
                    return { label: rank, percentage: response?.percentage || 0 }
                  }).filter(r => r.percentage > 0)
                }

                // For Total view, use topline totals; for breaks, calculate from segments
                const toplineTotals = TOPLINE_TOTALS[topic]
                const useToplineTotals = !isBreakActive && toplineTotals

                return {
                  value,
                  filterKey,
                  mostSegments: getMostSegments(),
                  leastSegments: getLeastSegments(),
                  mostTotal: useToplineTotals ? toplineTotals.most : null,
                  leastTotal: useToplineTotals ? toplineTotals.least : null,
                  sampleSize: mostData.sampleSize,
                }
              }).filter(row => row !== null)

              if (rows.length === 0) return null

              return (
                <div key={topic} className="border-b border-slate-100 dark:border-slate-700 pb-6 last:border-0 last:pb-0">
                  <h3 className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-3 text-center">
                    {label}
                  </h3>

                  <div className="space-y-2">
                    {rows.map(row => {
                      // Use topline totals if available, otherwise calculate and round to whole number
                      const mostTotal = row!.mostTotal ?? Math.round(row!.mostSegments.reduce((sum, s) => sum + s.percentage, 0))
                      const leastTotal = row!.leastTotal ?? Math.round(row!.leastSegments.reduce((sum, s) => sum + s.percentage, 0))

                      return (
                        <div key={row!.filterKey} className="flex items-center gap-2">
                          {isBreakActive && (
                            <div
                              className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0"
                              title={getDisplayName(row!.value)}
                            >
                              {getDisplayName(row!.value)}
                            </div>
                          )}
                          {/* Most Important (left bar) - grows toward center (right-to-left) - GREEN */}
                          <div className="flex-1">
                            <HorizontalStackedBar
                              segments={row!.mostSegments}
                              scaleType="ranking"
                              height={isBreakActive ? 24 : 32}
                              showLabels={false}
                              normalize={false}
                              direction="rtl"
                              colorGetter={(label) => MOST_COLORS[label] || '#8cd17d'}
                              maxScale={60}
                            />
                          </div>
                          {/* Center percentages */}
                          <div className="w-28 text-center shrink-0">
                            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                              {mostTotal}%
                              <span className="mx-1 text-slate-300 dark:text-slate-600">|</span>
                              {leastTotal}%
                            </span>
                          </div>
                          {/* Least Important (right bar) - grows from center outward (left-to-right) - RED */}
                          <div className="flex-1">
                            <HorizontalStackedBar
                              segments={row!.leastSegments}
                              scaleType="ranking"
                              height={isBreakActive ? 24 : 32}
                              showLabels={false}
                              normalize={false}
                              colorGetter={(label) => LEAST_COLORS[label] || '#e15759'}
                              maxScale={60}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })
          )}
        </div>

        {/* Rounding note */}
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
          *Totals may differ slightly from sum of bars due to rounding.
        </p>
      </div>
    </div>
  )
}
